
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React from 'react';

interface HowItWorksPageProps {
  onBack: () => void;
}

const HowItWorksPage: React.FC<HowItWorksPageProps> = ({ onBack }) => {
  return (
    <div className="min-h-dvh bg-[#050505] text-white font-sans selection:bg-emerald-500/30">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/50 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={onBack}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          >
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center text-white font-black text-lg shadow-[0_0_15px_rgba(16,185,129,0.4)]">
              Z
            </div>
            <span className="font-bold text-lg tracking-tight text-white">ZeroTrust</span>
          </button>
          
          <button 
            onClick={onBack}
            className="text-slate-400 hover:text-white transition-colors text-sm font-medium"
          >
            ← Back to Home
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-cyan-600/20 rounded-full blur-[120px] pointer-events-none"></div>
        
        <div className="max-w-5xl mx-auto relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-950/50 border border-cyan-500/30 text-cyan-300 text-xs font-bold uppercase tracking-widest mb-8">
            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></span>
            Technology
          </div>
          
          <h1 className="text-5xl md:text-6xl font-black tracking-tighter leading-[1.1] mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-slate-200 to-slate-500">
            How It Works
          </h1>
          
          <p className="text-xl text-slate-400 leading-relaxed mb-12 max-w-3xl mx-auto">
            Compliance Command combines cutting-edge AI, real-time simulation, and beautiful 3D visualization 
            to create an unprecedented learning experience.
          </p>
        </div>
      </section>

      {/* Core Technology Stack */}
      <section className="py-20 px-6 bg-gradient-to-b from-black to-slate-950">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 text-white">Technology Stack</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-indigo-500/30 transition-all">
              <div className="text-3xl mb-4">🧠</div>
              <h3 className="text-xl font-bold text-white mb-3">AI Risk Advisor</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Intelligent coaching system that generates dynamic missions, analyzes your city layout, 
                and provides context-aware guidance in real-time.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-emerald-500/30 transition-all">
              <div className="text-3xl mb-4">⚛️</div>
              <h3 className="text-xl font-bold text-white mb-3">React 19</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Latest React with concurrent features for smooth, responsive UI that handles 
                complex state management effortlessly.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-cyan-500/30 transition-all">
              <div className="text-3xl mb-4">🎨</div>
              <h3 className="text-xl font-bold text-white mb-3">Three.js</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                WebGL-powered 3D rendering for beautiful isometric visualization that runs 
                at 60fps in your browser.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-purple-500/30 transition-all">
              <div className="text-3xl mb-4">⚡</div>
              <h3 className="text-xl font-bold text-white mb-3">Vite</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Lightning-fast build tool with hot module replacement for instant development 
                feedback and optimized production builds.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-blue-500/30 transition-all">
              <div className="text-3xl mb-4">📘</div>
              <h3 className="text-xl font-bold text-white mb-3">TypeScript</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Type-safe code that catches errors before they happen, ensuring reliability 
                and maintainability at scale.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-pink-500/30 transition-all">
              <div className="text-3xl mb-4">🎭</div>
              <h3 className="text-xl font-bold text-white mb-3">TailwindCSS</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Utility-first CSS framework for rapid UI development with consistent, 
                beautiful design system.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Step by Step */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 text-white">The Experience</h2>
          
          <div className="space-y-16">
            {/* Step 1 */}
            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="flex-shrink-0 w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center text-3xl font-black shadow-[0_0_30px_rgba(79,70,229,0.4)]">
                1
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-white mb-3">Build Your City</h3>
                <p className="text-slate-400 leading-relaxed">
                  Start with an empty grid and construct your financial infrastructure. Place treasuries, 
                  firewalls, compliance centers, and more. Each building has unique properties and 
                  connectivity requirements—just like real systems.
                </p>
              </div>
              <div className="flex-shrink-0 w-64 h-48 bg-slate-900 rounded-xl border border-white/10 flex items-center justify-center">
                <div className="text-6xl">🏗️</div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex flex-col md:flex-row-reverse gap-8 items-center">
              <div className="flex-shrink-0 w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center text-3xl font-black shadow-[0_0_30px_rgba(16,185,129,0.4)]">
                2
              </div>
              <div className="flex-1 md:text-right">
                <h3 className="text-2xl font-bold text-white mb-3">Face Dynamic Challenges</h3>
                <p className="text-slate-400 leading-relaxed">
                  Our AI Director analyzes your city and generates personalized missions. Handle data breaches, 
                  regulatory audits, market crashes, and more. Every scenario teaches real compliance principles.
                </p>
              </div>
              <div className="flex-shrink-0 w-64 h-48 bg-slate-900 rounded-xl border border-white/10 flex items-center justify-center">
                <div className="text-6xl">⚠️</div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="flex-shrink-0 w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-600 to-blue-600 flex items-center justify-center text-3xl font-black shadow-[0_0_30px_rgba(6,182,212,0.4)]">
                3
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-white mb-3">Learn Through Feedback</h3>
                <p className="text-slate-400 leading-relaxed">
                  Watch your decisions play out in real-time. See how threats propagate through your network, 
                  how compliance decays, and how strategic placement makes all the difference. Instant visual 
                  feedback reinforces learning.
                </p>
              </div>
              <div className="flex-shrink-0 w-64 h-48 bg-slate-900 rounded-xl border border-white/10 flex items-center justify-center">
                <div className="text-6xl">📊</div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex flex-col md:flex-row-reverse gap-8 items-center">
              <div className="flex-shrink-0 w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-3xl font-black shadow-[0_0_30px_rgba(147,51,234,0.4)]">
                4
              </div>
              <div className="flex-1 md:text-right">
                <h3 className="text-2xl font-bold text-white mb-3">Master the System</h3>
                <p className="text-slate-400 leading-relaxed">
                  Progress through increasingly complex scenarios. Unlock advanced buildings, research new 
                  technologies, and develop sophisticated strategies. What starts simple becomes a deep, 
                  rewarding mastery experience.
                </p>
              </div>
              <div className="flex-shrink-0 w-64 h-48 bg-slate-900 rounded-xl border border-white/10 flex items-center justify-center">
                <div className="text-6xl">🏆</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 text-white">Key Features</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-indigo-950/30 to-purple-950/30 border border-indigo-500/20 rounded-xl p-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-lg bg-indigo-500/20 flex items-center justify-center text-2xl">
                  🎯
                </div>
                <h3 className="text-xl font-bold text-white">Real-time Simulation</h3>
              </div>
              <p className="text-slate-400 leading-relaxed">
                Every tick of the game clock runs sophisticated calculations: income, expenses, compliance decay, 
                threat propagation, and connectivity checks. See the consequences of your decisions immediately.
              </p>
            </div>

            <div className="bg-gradient-to-br from-emerald-950/30 to-teal-950/30 border border-emerald-500/20 rounded-xl p-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center text-2xl">
                  🗺️
                </div>
                <h3 className="text-xl font-bold text-white">Isometric 3D View</h3>
              </div>
              <p className="text-slate-400 leading-relaxed">
                Beautiful WebGL-powered visualization brings your city to life. Zoom, pan, and rotate to 
                inspect every detail. Visual indicators show connectivity, threats, and compliance status.
              </p>
            </div>

            <div className="bg-gradient-to-br from-cyan-950/30 to-blue-950/30 border border-cyan-500/20 rounded-xl p-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-lg bg-cyan-500/20 flex items-center justify-center text-2xl">
                  🎤
                </div>
                <h3 className="text-xl font-bold text-white">Voice Synthesis</h3>
              </div>
              <p className="text-slate-400 leading-relaxed">
                The AI Director doesn't just write text—it speaks. Generated audio brings missions and 
                coaching tips to life, creating an immersive experience that feels like a real advisor.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-950/30 to-pink-950/30 border border-purple-500/20 rounded-xl p-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center text-2xl">
                  📚
                </div>
                <h3 className="text-xl font-bold text-white">Scenario Library</h3>
              </div>
              <p className="text-slate-400 leading-relaxed">
                From tutorial campaigns to advanced challenges, our scenario system provides structured 
                learning paths. Each scenario teaches specific concepts with clear objectives and rewards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Innovation */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12 text-white">Technical Innovation</h2>
          
          <div className="bg-slate-900/50 border border-white/10 rounded-2xl p-10">
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-indigo-400 mb-3">Context-Aware AI</h3>
                <p className="text-slate-300 leading-relaxed">
                  Unlike static tutorials, our AI analyzes your actual city state—building positions, 
                  connectivity patterns, resource levels—and generates missions that make sense for 
                  your specific situation.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-emerald-400 mb-3">Performance Optimized</h3>
                <p className="text-slate-300 leading-relaxed">
                  Running complex simulations at 60fps requires careful optimization. We use efficient 
                  algorithms, memoization, and React's concurrent features to keep everything smooth 
                  even with hundreds of buildings.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-cyan-400 mb-3">Browser-First Architecture</h3>
                <p className="text-slate-300 leading-relaxed">
                  Everything runs in your browser—no server required. Your save data stays local, 
                  ensuring privacy and enabling offline play. We leverage modern web APIs for audio, 
                  storage, and rendering.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 px-6 bg-gradient-to-b from-black to-slate-950">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Ready to Experience It?</h2>
          <p className="text-xl text-slate-400 mb-10">
            See the technology in action. Build your first city and discover how learning can be engaging.
          </p>
          <button 
            onClick={onBack}
            className="px-10 py-4 bg-cyan-600 hover:bg-cyan-500 text-white font-bold rounded-full transition-all shadow-[0_0_30px_rgba(6,182,212,0.4)] text-lg"
          >
            Launch the Simulation →
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/10 bg-black">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2 opacity-50">
            <div className="w-6 h-6 rounded bg-slate-800 flex items-center justify-center font-bold text-xs">Z</div>
            <span className="font-bold text-sm">ZeroTrust</span>
          </div>
          <div className="text-xs text-slate-600">
            © 2025 Compliance Command. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HowItWorksPage;
